<script setup lang="ts">
import type { MaybeRefOrGetter } from 'vue';
import type { Entitlement } from '#layers/access/types/permissions/policy';
import { storeToRefs } from 'pinia';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePolicyEntitlements } from '#layers/access/composables/policy/use-policy-entitlements';
import { canReadSpace } from '#layers/access/constants/entitlements';
import { usePermissionsStore } from '#layers/access/stores/permissions';
import { customSpacesFlag } from '#layers/base/public';
import PolicyEntitlementResourceAction from './PolicyEntitlementResourceAction.vue';

const modelValue = defineModel<Entitlement[]>({ required: true });

// Entitlement resources gated behind a condition. A resource absent here is always shown.
const gatedResources: Record<string, MaybeRefOrGetter<boolean>> = {
	[canReadSpace().resource]: customSpacesFlag,
};

const { enabledActionsForResource } = usePolicyEntitlements({ entitlements: modelValue });
const { resources } = storeToRefs(usePermissionsStore());
const { t } = useI18n();

const visibleResources = computed(() => resources.value.filter((resource) => {
	const gate = gatedResources[resource.name];
	return gate === undefined || toValue(gate);
}));

const items = computed(() => visibleResources.value.map((resource) => ({ label: t(`policy.entitlements.${resource.name}.title`), ...resource })));
</script>

<template>
	<div class="flex flex-col gap-4">
		<DNotice color="neutral" :description="$t('policy.entitlements.explainer')" />
		<DAccordion
			:items
			type="multiple"
			class="rounded-sm border"
			:ui="{ content: 'divide-y border-t' }"
		>
			<!-- The label slot renders inside the accordion trigger, so it can carry the trigger's test hook. -->
			<template #default="{ item: resource }">
				<span :data-testid="`entitlement-resource-${resource.name}`">{{ resource.label }}</span>
			</template>
			<template #content="{ item: resource }">
				<div
					v-for="action in resource.actions"
					:key="action.name"
					class="flex h-12 py-2 ps-4 pe-6"
				>
					<PolicyEntitlementResourceAction v-model="modelValue" :resource :action />
				</div>
			</template>
			<template #trailing="{ item: resource }">
				<DBadge
					variant="soft"
					:color="enabledActionsForResource(resource).length > 0 ? 'primary' : 'neutral'"
					:label="`${enabledActionsForResource(resource).length}/${resource.actions.length}`"
				/>
			</template>
		</DAccordion>
	</div>
</template>
